import { useState } from 'react';
import { Modal, Input, GoldBtn } from './UI';
import { signIn, signUp, signOut, supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

export default function AuthModal({ open, onClose }) {
  const { profile } = useAuth();
  const [tab, setTab]         = useState('login');
  const [form, setForm]       = useState({ username: '', email: '', password: '' });
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const switchTab = t => { setTab(t); setError(''); setSuccess(''); };

  const handleForgotPassword = async () => {
    if (!form.email.trim()) { setError('Please enter your email address.'); return; }
    setLoading(true); setError(''); setSuccess('');
    const { error } = await supabase.auth.resetPasswordForEmail(form.email.trim(), {
      redirectTo: window.location.origin + '/?reset=true',
    });
    setLoading(false);
    if (error) { setError(error.message); return; }
    setSuccess('✓ Password reset email sent! Check your inbox and follow the link.');
  };

  const handleSubmit = async () => {
    setError(''); setSuccess(''); setLoading(true);
    try {
      if (tab === 'signup') {
        if (!form.username.trim() || !form.email.trim() || !form.password.trim())
          return setError('All fields are required.');
        if (form.username.length < 3)
          return setError('Username must be at least 3 characters.');
        if (form.password.length < 6)
          return setError('Password must be at least 6 characters.');
        if (!/^[a-zA-Z0-9_]+$/.test(form.username.trim()))
          return setError('Username can only contain letters, numbers, and underscores.');
        const { error } = await signUp(form.email.trim(), form.password, form.username.trim());
        if (error) {
          if (error.message.toLowerCase().includes('already registered') || error.message.toLowerCase().includes('already exists'))
            return setError('An account with this email already exists. Try logging in.');
          return setError(error.message);
        }
        setSuccess('✓ Account created! You can now log in.');
        switchTab('login');
        setForm(p => ({ ...p, password: '' }));
      } else {
        const { error } = await signIn(form.email.trim(), form.password);
        if (error) {
          if (error.message.toLowerCase().includes('email not confirmed'))
            return setError('Please confirm your email first. Check your inbox.');
          return setError('Invalid email or password.');
        }
        onClose();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    onClose();
    window.history.replaceState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const handleKeyDown = e => { if (e.key === 'Enter') tab === 'forgot' ? handleForgotPassword() : handleSubmit(); };

  if (profile) {
    return (
      <Modal open={open} onClose={onClose} title="Your Account" width={400}>
        <div style={{ textAlign: 'center', padding: '8px 0 24px' }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%', margin: '0 auto 16px',
            background: 'linear-gradient(135deg, var(--gold-dim), var(--gold))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 28, fontWeight: 700, color: 'var(--black)',
            fontFamily: "'Playfair Display', serif",
          }}>
            {profile.username?.[0]?.toUpperCase() || '?'}
          </div>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: 'var(--text)', marginBottom: 6 }}>
            {profile.username}
          </div>
          {profile.role === 'admin' && (
            <div style={{
              display: 'inline-block', background: 'rgba(212,175,55,0.12)',
              border: '1px solid var(--gold-dim)', borderRadius: 20, padding: '3px 12px',
              color: 'var(--gold)', fontSize: 13, fontWeight: 600, marginBottom: 8,
            }}>★ Administrator</div>
          )}
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 24 }}>
            Member since {new Date(profile.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
          </div>
          {profile.role === 'admin' && (
            <div style={{ marginBottom: 12 }}>
              <GoldBtn onClick={() => {
                onClose();
                window.history.pushState({}, '', '/admin');
                window.dispatchEvent(new PopStateEvent('popstate'));
              }}>★ Open Admin Panel</GoldBtn>
            </div>
          )}
          <GoldBtn onClick={handleSignOut} outline>Sign Out</GoldBtn>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={onClose}
      title={tab === 'login' ? 'Welcome Back' : tab === 'signup' ? 'Join Critiqr' : 'Reset Password'}
      width={440}>

      {tab !== 'forgot' && (
        <div style={{ display: 'flex', background: 'var(--surface2)', borderRadius: 8, padding: 3, marginBottom: 24, gap: 3 }}>
          {[['login','Log In'],['signup','Create Account']].map(([t,label]) => (
            <button key={t} onClick={() => switchTab(t)} style={{
              flex: 1, padding: '9px', border: 'none', borderRadius: 6,
              background: tab === t ? 'var(--surface3)' : 'transparent',
              color: tab === t ? 'var(--gold)' : 'var(--text-dim)',
              fontSize: 15, cursor: 'pointer', fontFamily: "'Crimson Text', serif",
              fontWeight: tab === t ? 600 : 400, transition: 'all 0.2s',
              borderBottom: tab === t ? '1px solid var(--gold-dim)' : '1px solid transparent',
            }}>{label}</button>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }} onKeyDown={handleKeyDown}>
        {tab === 'forgot' && (
          <>
            <p style={{ color: 'var(--text-dim)', fontSize: 15, lineHeight: 1.6, margin: '0 0 4px' }}>
              Enter your email and we'll send you a link to reset your password.
            </p>
            <Input label="Email" type="email" value={form.email} onChange={f('email')} placeholder="you@example.com" />
          </>
        )}
        {tab !== 'forgot' && (
          <>
            {tab === 'signup' && <Input label="Username" value={form.username} onChange={f('username')} placeholder="cinephile42" />}
            <Input label="Email" type="email" value={form.email} onChange={f('email')} placeholder="you@example.com" />
            <Input label="Password" type="password" value={form.password} onChange={f('password')} placeholder="••••••••" />
          </>
        )}

        {error && <div style={{ color:'var(--red)', fontSize:14, padding:'10px 12px', background:'rgba(192,57,43,0.1)', borderRadius:6, border:'1px solid rgba(192,57,43,0.2)' }}>{error}</div>}
        {success && <div style={{ color:'var(--green)', fontSize:14, padding:'10px 12px', background:'rgba(39,174,96,0.1)', borderRadius:6, border:'1px solid rgba(39,174,96,0.2)', lineHeight:1.5 }}>{success}</div>}

        <GoldBtn onClick={tab === 'forgot' ? handleForgotPassword : handleSubmit} disabled={loading} style={{ marginTop: 4 }}>
          {loading ? 'Please wait…' : tab === 'login' ? 'Log In' : tab === 'signup' ? 'Create Account' : 'Send Reset Email'}
        </GoldBtn>

        {tab === 'login' && (
          <div style={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:6 }}>
            <button onClick={() => switchTab('signup')} style={{ background:'none', border:'none', color:'var(--gold)', cursor:'pointer', fontSize:13 }}>Create an account</button>
            <button onClick={() => switchTab('forgot')} style={{ background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:13 }}>Forgot password?</button>
          </div>
        )}
        {tab === 'signup' && (
          <p style={{ textAlign:'center', fontSize:13, color:'var(--text-muted)', marginTop:4 }}>
            Already have an account?{' '}
            <button onClick={() => switchTab('login')} style={{ background:'none', border:'none', color:'var(--gold)', cursor:'pointer', fontSize:13 }}>Log in instead</button>
          </p>
        )}
        {tab === 'forgot' && (
          <p style={{ textAlign:'center', fontSize:13, color:'var(--text-muted)', marginTop:4 }}>
            Remembered it?{' '}
            <button onClick={() => switchTab('login')} style={{ background:'none', border:'none', color:'var(--gold)', cursor:'pointer', fontSize:13 }}>Back to Log In</button>
          </p>
        )}
      </div>
    </Modal>
  );
}
